/**
 * Add User Form Schema
 * 
 * Defines the validation rules for inviting new users to the system.
 * 
 * @module forms/users/addUser/schema
 * 
 * @remarks
 * - User ID is auto-generated by the backend
 * - userStatus is omitted on create; the backend defaults new users to 'active'
 *   (backend user_status enum is active | suspended | archived — there is no
 *   'invitationPending' status)
 * - Form metadata (name, title, description) is loaded from form.yml
 * - Follows DRY principles: labels in YAML, validation in Zod
 * 
 * @example
 * ```typescript
 * import { AddUserFormSchema, transformToApiRequest } from './schema'
 * 
 * const formData = {
 *   firstName: 'John',
 *   lastName: 'Smith',
 *   emailAddress: 'john@example.com'
 * }
 * 
 * const result = AddUserFormSchema.safeParse(formData)
 * if (result.success) {
 *   const apiRequest = transformToApiRequest(result.data)
 * }
 * ```
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// ============================================================================
// Form Schema (What user fills in)
// ============================================================================

export const AddUserFormSchema = z.object({
  // Required: First name (1-100 characters)
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(20, 'First name cannot exceed 20 characters')
    .trim(),

  // Required: Last name (1-20 characters)
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(20, 'Last name cannot exceed 20 characters')
    .trim(),

  // Required: Email address (valid email format)
  emailAddress: z
    .string()
    .min(1, 'Email address is required')
    .email('Invalid email address format')
    .max(254, 'Email address cannot exceed 254 characters')
    .trim(),

  // Optional: Phone number in E.164 format (e.g. "+912345678901")
  phone: z
    .string()
    .optional(),

  // Required: Organizations (array of organization IDs) — at least one.
  // The backend domain rule enforces "every user must belong to >= 1 organization"
  // (removeOrganizationFromUser returns 400 when removing a user's last org), so the
  // frontend must require one to avoid creating invariant-violating org-less users.
  organizations: z
    .array(z.string())
    .min(1, 'Select at least one organization')
    .default([]),

  // Required: Tenants (at least one of conex/farmgrid)
  tenants: z
    .array(z.enum(['conex', 'farmgrid']))
    .min(1, 'Select at least one tenant')
    .default(['conex']),

  // Optional: Profile picture. The uploaded value is an opaque reference — a short
  // objectKey in staging/production, or an inline `data:` URL in development mock mode
  // (no S3 backend). Enforce the backend's 2048-char cap for real keys/URLs, but exempt
  // `data:` URLs so the dev-mock data URL doesn't invalidate the form and block submit.
  profilePicture: z
    .string()
    .refine(
      (v) => v.startsWith('data:') || v.length <= 2048,
      'Profile picture URL cannot exceed 2048 characters',
    )
    .optional()
    .or(z.literal('')),
})

export type AddUserFormData = z.infer<typeof AddUserFormSchema>

/**
 * Transform form data to API request format
 *
 * Converts flat form structure to nested API structure and sets initial status.
 *
 * @param formData - Validated form data from AddUserFormSchema
 * @returns API request object ready for backend submission
 *
 * @remarks
 * - userStatus is omitted — backend defaults new users to 'active'
 *   (no 'invitationPending' status exists; user_status is active|suspended|archived)
 * - Filters out empty optional fields (undefined instead of empty strings)
 * - Maps frontend field names to backend field names (userName, emailId)
 * - API validation expects Title Case: "Active" | "Suspended" | "Archived"
 * - Backend converts Title Case to UPPERCASE for database storage
 * - Note: Job roles were removed from the invite flow (legacy job-role removal); a user's
 *   access is granted via account memberships on the user detail page instead
 * - Note: Organizations are handled separately via addOrganizationToUser endpoint
 */
// G20 codes sorted longest-first to avoid prefix collisions (e.g. +1 vs +966)
const G20_CODES = ['+966', '+961', '+44', '+86', '+91', '+62', '+39', '+81', '+52', '+55', '+61', '+33', '+49', '+82', '+27', '+90', '+54', '+7', '+1']

function parsePhoneE164(phone?: string): { countryCode?: string; mobileNumber?: string } {
  if (!phone || !phone.startsWith('+')) return {}
  for (const code of G20_CODES) {
    if (phone.startsWith(code)) {
      const number = phone.slice(code.length)
      return number ? { countryCode: code.slice(1), mobileNumber: number } : {}
    }
  }
  return {}
}

export function transformToApiRequest(formData: AddUserFormData, userName?: string) {
  const { countryCode, mobileNumber } = parsePhoneE164(formData.phone)
  // Only send a real stored reference (objectKey / URL). A `data:` URL is a dev-mock
  // artifact (no S3) and would exceed the backend's 2048-char cap — omit it.
  const profilePicture =
    formData.profilePicture && !formData.profilePicture.startsWith('data:')
      ? formData.profilePicture
      : undefined
  return {
    firstName: formData.firstName,
    lastName: formData.lastName,
    // userName must be UNIQUE (backend enforces UNIQUE(user_name)). First/last name may
    // legitimately repeat, so the composer generates a unique userName and passes it in;
    // fall back to "First Last" only if no override is supplied.
    userName: userName ?? `${formData.firstName} ${formData.lastName}`,
    emailAddress: formData.emailAddress,
    countryCode,
    mobileNumber,
    profilePicture,
    // Backend requires at least one tenant (conex/farmgrid); selected by the user.
    tenants: formData.tenants,
  }
}
